import React, { type FC, type JSX, useContext } from 'react';
import { useTranslation } from '@/app/components/translations/Translation.tsx';
import { useCommonClient } from '@/app/common/commonClientProvider.tsx';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import type { PROMOTION_TYPES } from '@/domainLogic/index.ts';
import { SearchModes } from '@/domainLogic/index.ts';
import usePriceMode from '@/app/components/search/hooks/usePriceMode.ts';
import {
  PriceAmount,
  PriceAmountGroup,
  PriceDetailsContainer,
  PriceDiscount,
  PriceTotalAmount,
  PriceUnit,
  ProductInfo,
} from './styled';
import type { DealCard } from '../../../../../utils/buildDealCards/dealCardTypes';
import { CardsContext } from '../../../../SearchPageContainer/ListContainer/CardsGrid/CardsContext';
import { getPriceDetails } from '../../../utils/getPriceDetails';

type PriceDetailsProps = {
  isOverlay: boolean;
  dealCard: DealCard;
  promotionType: PROMOTION_TYPES | null;
  showTotalPrice: boolean;
  showDoublePrice: boolean;
};

const PriceDetails: FC<PriceDetailsProps> = ({
  isOverlay,
  dealCard,
  promotionType,
  showTotalPrice,
  showDoublePrice,
}): JSX.Element => {
  const {
    searchInfoData,
    localization,
    commonData,
    localization: {
      configValuesApp: { lastMinuteDeals },
    },
  } = usePageWrapperData();
  const { t } = useTranslation();
  const { isMobile } = useCommonClient();
  const { isWhiteLabel } = useContext(CardsContext);
  const { totalPriceMode, showTotalPrice: prefersTotalPrice } = usePriceMode();
  const isHO = searchInfoData?.searchMode === SearchModes.HO;
  const hasPackageSavings = dealCard?.product?.rate?.packageDiscount?.enabled;
  const {
    productLabel,
    price,
    priceLabel,
    priceBeforeDiscount,
    totalPriceLabel,
    totalPrice,
    totalPriceBeforeDiscount,
    isHoOneNightStay,
    showDiscount,
    totalPerNightsLabel,
  } = getPriceDetails({
    dealCard,
    searchInfoData,
    localization,
    commonData,
    isMobile,
    isOverlay,
    t,
  });

  const showPriceTotalAmount = showTotalPrice && !isHO;
  // A legacy family total keeps the per-person strikethrough it has always shown.
  const showTotalStrikethrough = totalPriceMode && prefersTotalPrice && !isHO;
  // Whether a card shows a discount stays the per-person decision (showDiscount) it has always been —
  // the loyalty/MyPrice percentage badge is derived from it too — so total mode only swaps the amount.
  const priceDiscountAmount = showTotalStrikethrough ? totalPriceBeforeDiscount : priceBeforeDiscount;
  // In total mode a strikethrough that isn't actually lower resolves to null, so hide the line rather
  // than print a per-person amount next to a total. Off the toggler the pre-existing behaviour stands,
  // empty element included, so control renders byte-identical.
  const showPriceDiscount = showDiscount && !hasPackageSavings && (!showTotalStrikethrough || !!priceDiscountAmount);
  const showDoublePriceAmount = showDoublePrice && !isHO;
  // Only the two experiments relabel the amount. The legacy family total predates them and stays
  // bare, so control renders exactly as before.
  const showTotalUnitLabel = showPriceTotalAmount && totalPriceMode;
  const priceAmount = isHoOneNightStay ? totalPrice : price;
  const showProductInfo = false;
  const samePpAndTotalPrice = showPriceTotalAmount && price === totalPrice;

  const basePriceProps = {
    promotionType,
    isWhiteLabel,
    lastMinuteDeals,
    isOverlay,
  };

  const showDiscountAfterPrice = isMobile || isOverlay;
  const product = showProductInfo ? <ProductInfo {...basePriceProps}>{productLabel}</ProductInfo> : null;
  const discount = showPriceDiscount ? (
    <PriceDiscount {...basePriceProps} data-testid="price-discount">
      {priceDiscountAmount}
    </PriceDiscount>
  ) : null;
  const discountBefore = showDiscountAfterPrice ? null : discount;
  const discountAfter = showDiscountAfterPrice ? discount : null;
  const ppAmount = priceAmount ? <PriceAmount {...basePriceProps}>{priceAmount}</PriceAmount> : null;
  const totalAmount =
    showPriceTotalAmount || showDoublePriceAmount ? (
      <PriceTotalAmount isOverlay={isOverlay}>{totalPriceLabel}</PriceTotalAmount>
    ) : null;
  const label = priceLabel ? <PriceUnit {...basePriceProps}>&nbsp;{priceLabel}</PriceUnit> : null;
  const totalLabel = showTotalUnitLabel ? (
    <PriceUnit {...basePriceProps}>&nbsp;{t('vacation.oss.cards.total')}</PriceUnit>
  ) : null;
  const ppGroup = (
    <PriceAmountGroup data-testid="price-amount-group">
      {ppAmount}
      {label}
    </PriceAmountGroup>
  );
  const totalGroup = (
    <PriceAmountGroup data-testid="price-amount-group">
      <PriceAmount {...basePriceProps}>{totalPrice}</PriceAmount>
      {totalLabel}
    </PriceAmountGroup>
  );
  const totalPerNights = totalPerNightsLabel ? (
    <PriceTotalAmount isOverlay={isOverlay} style={{ marginTop: 4 }}>
      {totalPerNightsLabel}
    </PriceTotalAmount>
  ) : null;

  const priceLayout = () => {
    if (showDoublePriceAmount) {
      return (
        <>
          {product}
          {discountBefore}
          {ppGroup}
          {discountAfter}
          {totalAmount}
        </>
      );
    }

    if (showPriceTotalAmount) {
      return (
        <>
          {product}
          {discountBefore}
          {totalGroup}
          {discountAfter}
        </>
      );
    }

    if (samePpAndTotalPrice) {
      return (
        <>
          {discountBefore}
          {totalGroup}
          {discountAfter}
        </>
      );
    }

    return (
      <>
        {product}
        {discountBefore}
        {ppGroup}
        {discountAfter}
        {totalPerNights}
      </>
    );
  };

  return <PriceDetailsContainer isOverlay={isOverlay}>{priceLayout()}</PriceDetailsContainer>;
};

export default PriceDetails;
